// Self-hosted OTA update metadata for RealityOperator, served tokenlessly from
// the public Supabase Storage `software` bucket — the same bucket + pattern the
// Electron installer self-update uses (electron/main.ts STORAGE_BASE), under an
// `operator-android/` prefix.
//
// Layout published by scripts/publish-android-update.js:
//   operator-android/index.json                  -> { latest, versions[] }   (dropdown source)
//   operator-android/<version>/manifest.json      -> VersionManifest          (per-version)
//   operator-android/<version>/bundle.zip         -> Capawesome web bundle
//   operator-android/<version>/RealityOperator.apk (optional; native shell)

// The bucket follows the build's Supabase project (baked in by Vite), so a
// staging build reads/applies OTA bundles from the STAGING project's bucket and
// a prod build from prod — same separate-project channel model as the desktop
// self-update (electron/main.ts). Fallback covers tests/older envs only.
const SUPABASE_URL =
  (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/+$/, '') ||
  'https://qaqskfvvfjewmfmkdchq.supabase.co';
export const OPERATOR_UPDATE_BASE = `${SUPABASE_URL}/storage/v1/object/public/software/operator-android`;

export interface VersionManifest {
  version: string;
  bundleUrl: string;
  checksum?: string; // SHA-256 hex of bundle.zip (verified by Live Update on download)
  size?: number;
  notes?: string;
  publishedAt?: string;
  // Present only when this version also ships a new native shell (native code /
  // plugin / permission change) that an OTA web-bundle swap cannot deliver.
  apkUrl?: string;
  apkVersionCode?: number;
}

export interface VersionIndexEntry {
  version: string;
  publishedAt?: string;
  notes?: string;
}

export interface VersionIndex {
  latest: string | null;
  versions: VersionIndexEntry[];
}

// Cache-busted fetch — Storage sets long cache headers, but update checks must
// see freshly-published manifests.
async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export function fetchVersionIndex(): Promise<VersionIndex | null> {
  return fetchJson<VersionIndex>(`${OPERATOR_UPDATE_BASE}/index.json`);
}

export function fetchVersionManifest(version: string): Promise<VersionManifest | null> {
  const v = version.replace(/^v/, '');
  return fetchJson<VersionManifest>(`${OPERATOR_UPDATE_BASE}/${v}/manifest.json`);
}

// Semver-ish compare (X.Y.Z, ignores any leading 'v' and pre-release suffix).
// Returns >0 if a>b, <0 if a<b, 0 if equal. Mirrors electron/main.ts semantics.
export function compareVersions(a: string, b: string): number {
  const parse = (s: string) =>
    s.replace(/^v/, '').split('-')[0].split('.').map((n) => parseInt(n, 10) || 0);
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d > 0 ? 1 : -1;
  }
  return 0;
}

export function isNewerVersion(candidate: string, current: string): boolean {
  return compareVersions(candidate, current) > 0;
}

export function versionsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return compareVersions(a, b) === 0;
}
